import React, { FC, useState } from 'react'
import { Collapse, Icon, Table } from 'antd'

import { consumersService } from '../../services'
import { ReportEndCustomer, ReportEntry, Consumer, RequestErrorInterface, RequestErrorObject, SingleResponse } from '../../types'
import handleError from '../handleError'

import './index.less'

interface ReportTableProps {
	reportEntry: ReportEntry
}

interface ReportCustomerProps {
	customer: ReportEndCustomer
}

interface ConsumerReport {
	[serial: string]: Consumer[]
}

const { Panel } = Collapse

const handleReportError = (error: RequestErrorInterface): RequestErrorObject => {
	const source = 'Report Error'
	const base = { source, status: error.response.status }

	return { ...base, description: 'There was an unexpected problem when attempting to retrieve the report' }
}

const RealtimeReport: FC<ReportTableProps> = ({ reportEntry: reseller }) => {
	const resellerHasCustomers = reseller.end_customers?.length > 0

	return (
		<Collapse key={reseller.id} defaultActiveKey={[reseller.id]}>
			<Panel
				key={reseller.id}
				header={
					<>
						<Icon type='apartment' /> {reseller.name}
					</>
				}
				className='reseller-panel'
			>
				{resellerHasCustomers ? (
					reseller.end_customers.map(customer => <EndCustomerCollapse key={customer.id} customer={customer} />)
				) : (
					<div className='no-results'>No end customers assigned</div>
				)}
			</Panel>
		</Collapse>
	)
}

const EndCustomerCollapse: FC<ReportCustomerProps> = ({ customer }) => {
	const [consumersData, setConsumersData] = useState<ConsumerReport>({})
	const [consumersLoading, setConsumersLoading] = useState(false)

	const fetchLicenseConsumers = async (customerId: number, serial: string) => {
		setConsumersLoading(true)

		try {
			const consumers: SingleResponse<Consumer[]> = await consumersService.get(customerId, serial)
			setConsumersData(prevState => ({
				...prevState,
				[serial]: consumers.data
			}))
		} catch (error) {
			handleError(error, handleReportError)
		} finally {
			setConsumersLoading(false)
		}
	}

	const renderLicenseConsumers = (serial: string) => (
		<Table
			className='consumer-table'
			loading={consumersLoading}
			pagination={false}
			dataSource={consumersData?.[serial]}
			columns={[{ title: 'Subscribers', dataIndex: 'username' }]}
		/>
	)

	const customerHasLicenses = customer.licenses?.length > 0
	const customerLicensesColumns = [
		{ title: 'License Type', dataIndex: 'display_name', render: (text: string) => <strong>{text}</strong> },
		{ title: 'Total', dataIndex: 'quantity' },
		{ title: 'Consumed', dataIndex: 'consumed' }
	]

	return (
		<Collapse key={customer.id} defaultActiveKey={[customer.id]}>
			<Panel
				header={
					<>
						<Icon type='user' /> {customer.name}
					</>
				}
				key={customer.id}
				className='customer-panel'
			>
				{customerHasLicenses ? (
					<Table
						rowKey='display_name'
						dataSource={customer.licenses}
						columns={customerLicensesColumns}
						pagination={false}
						expandedRowRender={({ serial }) => renderLicenseConsumers(serial)}
						onExpand={(_, record) => fetchLicenseConsumers(customer.id, record.serial)}
						expandIcon={({ record, onExpand, expanded }) => {
							return record.consumed ? <Icon type={expanded ? 'minus-square' : 'plus-square'} onClick={e => onExpand(record, e)} /> : null
						}}
					/>
				) : (
					<div className='no-results'>No licenses attached</div>
				)}
			</Panel>
		</Collapse>
	)
}

export default RealtimeReport
